import {Box, Grid} from '@mui/material';
import {FC, useEffect} from 'react';
import {useIntl} from 'react-intl';
import {useNavigate, useParams} from 'react-router-dom';
import {invoicesApi} from 'src/api';
import {useBusinessId, useAppSelector} from 'src/app/hooks';
import {store} from 'src/app/store';
import {PageHeader} from 'src/components/common/PageHeader';
import {DownloadInvoicePdf} from 'src/components/invoices/InvoicesTable/DownloadInvoicePdf';
import {InvoiceDetails} from 'src/components/invoices/InvoiceDetails';
import {InvoiceTransactions} from 'src/components/invoices/InvoiceTransactions';
import {LinkedInvoiceItems} from 'src/components/invoices/LinkedInvoiceItems';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {isErrorObject} from 'src/utils/helper';
import {NotifyError} from 'src/utils/notification';
import {headerSx} from './InvoiceInfo.sx';

export const InvoiceInfo: FC = () => {
    const {id} = useParams();
    const intl = useIntl();
    const navigate = useNavigate();
    const {authInfo} = useAppSelector((state) => state.common);
    const [trigger, response] = invoicesApi.useLazyGetInvoiceByIdQuery();

    const businessId = useBusinessId();

    useEffect(() => {
        // A missing path segment is a malformed URL, not a permission question - PermissionRoute has
        // already decided whether this page may be opened at all.
        if (!id) {
            navigate('/404');
            return;
        }

        const fetchInvoice = async () => {
            // The request carries the invoice id and the business, nothing else. The tenant fields
            // this used to add by role - userId for a driver, companyId for a company, realEstateId for
            // a property manager - are written by the server from the caller's binding now, and it
            // overwrites whatever arrives.
            store.dispatch(preloaderChangeStatus(true));
            try {
                await trigger({invoiceId: +id, businessId}).unwrap();
            } catch (error) {
                if (isErrorObject(error)) {
                    NotifyError(error.data.message);
                }
                navigate('/app/invoices');
            } finally {
                store.dispatch(preloaderChangeStatus(false));
            }
        };

        void fetchInvoice();
    }, [id]);

    const invoice = response.data;

    if (!invoice) {
        return <Box/>;
    }

    const title = `${intl.formatMessage({id: 'invoice'})} ${invoice.invoiceNumber ?? invoice.id}`;

    return (
        <Box>
            <Box sx={headerSx}>
                <PageHeader
                    title={title}
                    onlyTitle
                />
                {invoice.bexioId && (
                    <DownloadInvoicePdf
                        name={invoice.invoiceNumber}
                        invoiceId={invoice.id}
                        businessId={businessId}
                        userId={invoice.user?.id}
                        isIcon
                    />
                )}
            </Box>
            <Grid
                container
                spacing={2}
            >
                <Grid
                    item
                    xs={12}
                >
                    <InvoiceDetails data={invoice}/>
                </Grid>
                <Grid
                    item
                    xs={12}
                >
                    <InvoiceTransactions
                        invoiceId={invoice.id}
                        businessId={businessId}
                    />
                </Grid>
                <Grid
                    item
                    xs={12}
                >
                    <LinkedInvoiceItems
                        items={invoice.customInvoiceItems}
                        currency={invoice.currency}
                    />
                </Grid>
            </Grid>
        </Box>
    );
};
